import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Flame, Bell, Info, Users, Aperture, Trophy, Film } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useStars } from '@/hooks/useStars';
import { useNotifications } from '@/hooks/useNotifications';
import { useSubscriptionContext as useSubscription } from '@/contexts/SubscriptionContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { useProfile } from '@/hooks/useProfile';
import { ThemeToggle } from '@/components/ThemeToggle';
import { CapacityIndicator } from '@/components/dashboard/CapacityIndicator';
import { useTasks } from '@/hooks/useTasks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ReferralModal } from '@/components/ReferralModal';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { UserAvatarWithFrame } from '@/components/rewards/UserAvatarWithFrame';
import { UserBadges } from '@/components/rewards/UserBadges';
import { OnboardingSlideshow, shouldShowOnboarding } from '@/components/OnboardingSlideshow';

export function AppHeader() {
  const navigate = useNavigate();
  const { profile: authProfile, user } = useAuth();
  const { userStars } = useStars();
  const { unreadCount } = useNotifications();
  const { isProActive } = useSubscription();
  const { t, language } = useTranslation();
  const [referralModalOpen, setReferralModalOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(() => shouldShowOnboarding());

  // Use TanStack Query for profile data - enables instant updates from cache
  const { data: cachedProfile } = useProfile(user?.id);
  
  // Prefer cached profile data for instant updates, fallback to auth profile
  const profile = cachedProfile || authProfile;

  const userName = profile?.display_name || user?.email?.split('@')[0] || t('guest');
  const isPro = isProActive;
  const { tasks } = useTasks();

  const todayTasks = useMemo(() => {
    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return tasks.filter(t => t.dueDate === todayStr && !t.archivedAt);
  }, [tasks]);

  // Get active frame and badges from profile
  const activeFrame = (profile as any)?.active_frame || null;
  const activeBadges = (profile as any)?.active_badges || [];

  return (
    <>
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            {/* Left: Avatar with frame, PRO badge and user badges */}
            <div className="flex items-center gap-2 shrink-0">
              <UserAvatarWithFrame
                avatarUrl={profile?.avatar_url}
                displayName={userName}
                frameId={activeFrame}
                size="md"
                onClick={() => navigate('/profile')}
                showProBadge={isPro}
              />
              <UserBadges badgeIds={activeBadges} maxDisplay={2} size="sm" />
            </div>

            {/* Center: Streak + Capacity */}
            <div className="flex items-center gap-2">
              <CapacityIndicator todayTasks={todayTasks} />
              {userStars && userStars.current_streak_days > 0 && (
                <Badge variant="outline" className="gap-1 px-2 py-1 border-orange-500/50">
                  <Flame className="h-3.5 w-3.5 text-orange-500" />
                  <span className="font-semibold text-sm">{userStars.current_streak_days}</span>
                </Badge>
              )}
            </div>

            {/* Right: Notifications + News + Users Catalog + Focus + Rating + Theme + Invite */}
            {/* Icon buttons shrunk 1.5x (h-10→h-7) to tighten spacing between icons */}
            <div className="flex items-center gap-0 [&_button[data-icon-btn]]:h-7 [&_button[data-icon-btn]]:w-7">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-icon-btn
                    onClick={() => navigate('/notifications')}
                    className="relative hover:bg-primary/10"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Уведомления
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-icon-btn
                    onClick={() => navigate('/infocenter')}
                    className="hover:bg-blue-500/10"
                  >
                    <Info className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {language === 'ru' ? 'Инфоцентр' : 'Infocenter'}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-icon-btn
                    onClick={() => navigate('/users')}
                    className="hover:bg-green-500/10"
                  >
                    <Users className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Каталог пользователей
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-icon-btn
                    onClick={() => navigate('/focus')}
                    className="hover:bg-purple-500/10"
                  >
                    <Aperture className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Фокус
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-icon-btn
                    onClick={() => navigate('/rating')}
                    className="hover:bg-yellow-500/10"
                  >
                    <Trophy className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('rating') || 'Рейтинг'}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-icon-btn
                    onClick={() => setOnboardingOpen(true)}
                    className="hover:bg-cyan-500/10"
                  >
                    <Film className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {language === 'ru' ? 'Обзор возможностей' : 'Feature Tour'}
                </TooltipContent>
              </Tooltip>

              <ThemeToggle />
              
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  onClick={() => setReferralModalOpen(true)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg shadow-amber-500/25 gap-2 px-4"
                  size="sm"
                >
                  <Gift className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('inviteFriend')}</span>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </header>

      <ReferralModal 
        open={referralModalOpen} 
        onOpenChange={setReferralModalOpen} 
      />

      <OnboardingSlideshow
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
      />
    </>
  );
}
